import type { LandingContent } from './landingContent'
import { tokenStore } from '@/lib/auth/tokenStore'
import { getPortalApiBase } from '@/lib/env'
import { prepareImageForUpload } from '@/shared/utils/imageUpload'

/**
 * Cliente del API del CMS del landing (backend portal-server, HTTP).
 * Reemplaza por completo al localStorage: el contenido vive en la BD.
 */
const ENDPOINT = `${getPortalApiBase()}/landing-content`

/**
 * Backstop de transporte: si el proxy cuelga la conexión a mitad de la subida
 * (p. ej. al exceder su límite de cuerpo) el `fetch` nunca resolvería y el
 * spinner quedaría girando para siempre. Un `AbortController` con timeout lo
 * fuerza a rechazar para que el llamador muestre el error.
 */
const UPLOAD_TIMEOUT_MS = 45_000

/**
 * Cabeceras con el Bearer del admin (mismo patrón que commercials y adminApi de
 * pagos). Guardar el landing y subir imágenes exige un admin
 * del portal verificado en portal-server (goal seguridad-remediacion 12). En la
 * subida NO se fija `Content-Type`: el navegador pone el boundary del FormData.
 */
function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = tokenStore.get()
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  }
}

/** Lee el contenido guardado. Devuelve null si aún no se ha guardado nada. */
export async function fetchLandingContent(): Promise<LandingContent | null> {
  const res = await fetch(ENDPOINT, { headers: { Accept: 'application/json' } })
  if (!res.ok) throw new Error(`GET landing-content: ${res.status}`)
  // La BD puede no tener contenido aún → el backend responde con cuerpo vacío.
  // Lo tratamos como "sin contenido" (null), no como un error.
  const text = await res.text()
  if (!text.trim()) return null
  const data = JSON.parse(text) as LandingContent | null
  return data && typeof data === 'object' ? data : null
}

/** Sube una imagen al backend (almacenamiento real) y devuelve su URL. */
export async function uploadImage(file: File): Promise<string> {
  // Recomprime/redimensiona en el cliente para caber bajo el límite del proxy y
  // valida el archivo (lanza un mensaje claro si no sirve).
  const prepared = await prepareImageForUpload(file)

  const form = new FormData()
  form.append('file', prepared)

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS)
  try {
    const res = await fetch(`${ENDPOINT}/upload`, {
      method: 'POST',
      headers: authHeaders(),
      body: form,
      signal: controller.signal,
    })
    if (!res.ok) {
      // El 400 de portal-server dice qué formatos admite (PNG, JPEG, GIF o
      // WebP; el SVG se rechaza desde el goal seguridad-remediacion 12).
      const body = (await res.json().catch(() => null)) as { message?: unknown } | null
      throw new Error(
        typeof body?.message === 'string' ? body.message : `POST landing-content/upload: ${res.status}`,
      )
    }
    const data = (await res.json()) as { url: string }
    return data.url
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('La subida tardó demasiado y se canceló. Reintenta.', {
        cause: err,
      })
    }
    throw err
  } finally {
    clearTimeout(timer)
  }
}

/** Guarda el contenido completo. */
export async function saveLandingContent(content: LandingContent): Promise<void> {
  const res = await fetch(ENDPOINT, {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(content),
  })
  if (!res.ok) throw new Error(`PUT landing-content: ${res.status}`)
}

/** Guarda UNA sección (no envía todo el documento). */
export async function saveLandingSection(
  section: keyof LandingContent,
  value: LandingContent[keyof LandingContent],
): Promise<void> {
  const res = await fetch(`${ENDPOINT}/${section}`, {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ value }),
  })
  if (!res.ok) throw new Error(`PUT landing-content/${section}: ${res.status}`)
}
